import {
  Button,
  ButtonGroup,
  HStack,
  FormErrorMessage,
  FormLabel,
  FormControl,
  Input,
  Heading,
  useDisclosure,
  Text,
  UnorderedList,
  ListItem,
} from '@chakra-ui/react';
import { Trans } from '@lingui/react/macro';
import { t } from '@lingui/core/macro';
import { SubmitHandler, useForm } from 'react-hook-form';
import { useState } from 'react';
import { ConfirmationDialog } from '@duke-hq/ui/components/DialogBox/ConfirmationDialog';
import { emailRegex, phoneRegex } from '@duke-hq/shared-constants/regexPatterns';
import {
  FormContentContainer,
  FormFooterContainer,
  FormMainContainer,
} from '@duke-hq/ui/components/Modals/FormModalLayouts';
import { usiIdentityChanged } from '@duke-hq/lib/usi';
import { UsiVerificationResetWarning } from './UsiVerificationResetWarning';

type BasicDetailsUpdateFormProps = {
  defaultFirstName: string;
  defaultSecondaryEmail: string | null;
  defaultLastName: string | null;
  defaultPreferredName?: string | null;
  defaultEmail: string | null;
  defaultPhoneNumber: string | null;
  defaultUniversalStudentIdentifier?: string | null;
  isUsiVerified?: boolean;
  updateBasicInfo: (values: BasicDetailsUpdateFormDataTypes) => void;
  onCancel: () => void;
  isDataSubmitting: boolean;
};

type BasicDetailsUpdateFormDataTypes = {
  firstName: string;
  lastName?: string;
  preferredName?: string;
  email?: string;
  secondaryEmail?: string;
  phoneNumber?: string;
  universalStudentIdentifier?: string;
};

export function BasicDetailsUpdateForm({
  defaultEmail,
  defaultSecondaryEmail,
  defaultFirstName,
  defaultLastName,
  defaultPhoneNumber,
  defaultPreferredName,
  defaultUniversalStudentIdentifier,
  isUsiVerified = false,
  updateBasicInfo,
  onCancel,
  isDataSubmitting,
}: BasicDetailsUpdateFormProps): React.ReactElement {
  const {
    handleSubmit,
    register,
    watch,
    formState: { errors },
  } = useForm<BasicDetailsUpdateFormDataTypes>();

  const { isOpen, onOpen, onClose } = useDisclosure();
  const [pendingData, setPendingData] = useState<BasicDetailsUpdateFormDataTypes | null>(null);
  const [missingFields, setMissingFields] = useState<string[]>([]);
  const [usiWarningShown, setUsiWarningShown] = useState(false);

  const validateRequiredFields = (values: BasicDetailsUpdateFormDataTypes) => {
    const missing: string[] = [];

    if (values.lastName === undefined || values.lastName.trim() === '') {
      missing.push(t`Last name`);
    }

    if (values.email === undefined || values.email.trim() === '') {
      missing.push(t`Email address`);
    }

    if (values.phoneNumber === undefined || values.phoneNumber.trim() === '') {
      missing.push(t`Phone number`);
    }

    return missing;
  };

  const stringOrNull = (value: unknown) => (typeof value === 'string' ? value : null);

  const willClearUsiVerification = (values: {
    firstName?: unknown;
    lastName?: unknown;
    universalStudentIdentifier?: unknown;
  }) =>
    isUsiVerified &&
    usiIdentityChanged(
      {
        firstName: defaultFirstName,
        lastName: defaultLastName,
        universalStudentIdentifier: defaultUniversalStudentIdentifier,
      },
      {
        firstName: stringOrNull(values.firstName) ?? '',
        lastName: stringOrNull(values.lastName),
        universalStudentIdentifier: stringOrNull(values.universalStudentIdentifier),
      },
    );

  const watchedIdentity = {
    firstName: stringOrNull(watch('firstName')) ?? '',
    lastName: stringOrNull(watch('lastName')),
    universalStudentIdentifier: stringOrNull(watch('universalStudentIdentifier')),
  };
  const usiWarningActive = usiWarningShown && willClearUsiVerification(watchedIdentity);

  const firstNameChanged = usiIdentityChanged(
    { firstName: defaultFirstName },
    { firstName: watchedIdentity.firstName },
  );
  const lastNameChanged = usiIdentityChanged(
    { lastName: defaultLastName },
    { lastName: watchedIdentity.lastName },
  );
  const usiChanged = usiIdentityChanged(
    { universalStudentIdentifier: defaultUniversalStudentIdentifier },
    { universalStudentIdentifier: watchedIdentity.universalStudentIdentifier },
  );

  const usiWarnedFieldStyle = (changed: boolean) =>
    usiWarningActive && changed
      ? { borderColor: 'orange.400', boxShadow: '0 0 0 1px var(--chakra-colors-orange-400)' }
      : {};

  const firstNameWarnStyle = usiWarnedFieldStyle(firstNameChanged);
  const lastNameWarnStyle = usiWarnedFieldStyle(lastNameChanged);
  const usiWarnStyle = usiWarnedFieldStyle(usiChanged);

  const onSubmit: SubmitHandler<BasicDetailsUpdateFormDataTypes> = (values) => {
    const clearsVerification = willClearUsiVerification(values);

    if (clearsVerification && !usiWarningShown) {
      setUsiWarningShown(true);
      return;
    }
    if (!clearsVerification) {
      setUsiWarningShown(false);
    }

    const missing = validateRequiredFields(values);

    if (missing.length > 0) {
      setMissingFields(missing);
      setPendingData(values);
      onOpen();
      return;
    }

    updateBasicInfo(values);
  };

  const handleAdminOverride = () => {
    if (pendingData) {
      updateBasicInfo(pendingData);
    }
    onClose();
    setPendingData(null);
    setMissingFields([]);
  };

  const handleCancelOverride = () => {
    onClose();
    setPendingData(null);
    setMissingFields([]);
  };

  return (
    <>
      <form onSubmit={handleSubmit(onSubmit)} style={{ width: '100%', height: '100%' }} noValidate>
        <FormMainContainer>
          <FormContentContainer>
            <Heading fontSize="lg" size="xs" w="full">
              <Trans>Update Student Basic Information</Trans>
            </Heading>

            {usiWarningActive && (
              <UsiVerificationResetWarning
                usiChanged={usiChanged}
                identityChanged={firstNameChanged || lastNameChanged}
              />
            )}

            <FormControl isInvalid={!!errors.firstName} isRequired>
              <FormLabel htmlFor="firstName">
                <Trans>First name</Trans>
              </FormLabel>
              <Input
                id="firstName"
                {...register('firstName', {
                  required: t`First name is required`,
                  setValueAs: (value) => typeof value === 'string' && value.trim(),
                })}
                type="text"
                spellCheck="false"
                autoComplete="off"
                autoFocus
                defaultValue={defaultFirstName}
                {...firstNameWarnStyle}
              />
              <FormErrorMessage>{errors.firstName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.lastName}>
              <FormLabel htmlFor="lastName">
                <Trans>Last name</Trans>
              </FormLabel>
              <Input
                id="lastName"
                {...register('lastName', {
                  setValueAs: (value) =>
                    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined,
                })}
                type="text"
                spellCheck="false"
                autoComplete="off"
                defaultValue={defaultLastName ?? ''}
                {...lastNameWarnStyle}
              />
              <FormErrorMessage>{errors.lastName?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.preferredName}>
              <FormLabel htmlFor="preferredName">
                <Trans>Preferred name</Trans>
              </FormLabel>
              <Input
                id="preferredName"
                {...register('preferredName', {
                  setValueAs: (value) =>
                    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined,
                })}
                type="text"
                spellCheck="false"
                autoComplete="off"
                defaultValue={defaultPreferredName ?? ''}
              />
              <FormErrorMessage>{errors.preferredName?.message}</FormErrorMessage>
            </FormControl>

            <HStack w="full" justify="space-between" align="start" gap="10px">
              <FormControl isInvalid={!!errors.email}>
                <FormLabel htmlFor="email">
                  <Trans>Email address</Trans>
                </FormLabel>
                <Input
                  id="email"
                  {...register('email', {
                    pattern: {
                      value: emailRegex,
                      message: t`Please enter a valid email address`,
                    },
                    setValueAs: (value) =>
                      typeof value === 'string' && value.trim().length > 0
                        ? value.trim()
                        : undefined,
                  })}
                  type="email"
                  spellCheck="false"
                  autoComplete="off"
                  defaultValue={defaultEmail ?? ''}
                />
                <FormErrorMessage>{errors.email?.message}</FormErrorMessage>
              </FormControl>

              <FormControl isInvalid={!!errors.phoneNumber}>
                <FormLabel htmlFor="phoneNumber">
                  <Trans>Phone number</Trans>
                </FormLabel>
                <Input
                  id="phoneNumber"
                  {...register('phoneNumber', {
                    pattern: {
                      value: phoneRegex,
                      message: t`Please enter a valid phone number`,
                    },
                    setValueAs: (value) =>
                      typeof value === 'string' && value.trim().length > 0
                        ? value.replace(/\s/g, '')
                        : undefined,
                  })}
                  type="tel"
                  spellCheck="false"
                  autoComplete="off"
                  defaultValue={defaultPhoneNumber ?? ''}
                />
                <FormErrorMessage>{errors.phoneNumber?.message}</FormErrorMessage>
              </FormControl>
            </HStack>

            <FormControl isInvalid={!!errors.secondaryEmail}>
              <FormLabel htmlFor="secondaryEmail">
                <Trans>Secondary email address</Trans>
              </FormLabel>
              <Input
                id="secondaryEmail"
                {...register('secondaryEmail', {
                  pattern: {
                    value: emailRegex,
                    message: t`Please enter a valid email address`,
                  },
                  setValueAs: (value) =>
                    typeof value === 'string' && value.trim().length > 0 ? value.trim() : undefined,
                })}
                type="email"
                spellCheck="false"
                autoComplete="off"
                defaultValue={defaultSecondaryEmail ?? ''}
              />
              <FormErrorMessage>{errors.secondaryEmail?.message}</FormErrorMessage>
            </FormControl>

            <FormControl isInvalid={!!errors.universalStudentIdentifier}>
              <FormLabel htmlFor="universalStudentIdentifier">
                <Trans>USI (if known)</Trans>
              </FormLabel>
              <Input
                id="universalStudentIdentifier"
                type="text"
                spellCheck="false"
                autoComplete="off"
                {...register('universalStudentIdentifier', {
                  pattern: {
                    value: /^[A-Za-z0-9]{10}$/,
                    message: t`Unique Student Identifier must be exactly 10 letters or numbers`,
                  },
                  setValueAs: (value) =>
                    typeof value === 'string' && value.trim().length > 0
                      ? value.trim().toUpperCase()
                      : undefined,
                })}
                defaultValue={defaultUniversalStudentIdentifier ?? ''}
                textTransform="uppercase"
                {...usiWarnStyle}
              />
              <FormErrorMessage>{errors.universalStudentIdentifier?.message}</FormErrorMessage>
            </FormControl>
          </FormContentContainer>

          <FormFooterContainer>
            <ButtonGroup>
              <Button variant="secondary" onClick={onCancel}>
                <Trans>Cancel</Trans>
              </Button>
              <Button
                isLoading={isDataSubmitting}
                type="submit"
                colorScheme={usiWarningActive ? 'orange' : undefined}
              >
                {usiWarningActive ? (
                  <Trans>Save and clear USI verification</Trans>
                ) : (
                  <Trans>Update</Trans>
                )}
              </Button>
            </ButtonGroup>
          </FormFooterContainer>
        </FormMainContainer>
      </form>

      <ConfirmationDialog
        isOpen={isOpen}
        onClose={handleCancelOverride}
        title={<Trans>Missing Required Information</Trans>}
        body={
          <>
            <Text mb={3}>
              <Trans>The following required fields are missing or empty:</Trans>
            </Text>
            <UnorderedList mb={4}>
              {missingFields.map((field) => (
                <ListItem key={field}>{field}</ListItem>
              ))}
            </UnorderedList>
            <Text color="danger.emphasis" fontWeight="medium">
              <Trans>
                You can still save these changes, but the student record will be incomplete. Do you
                want to continue?
              </Trans>
            </Text>
          </>
        }
        onConfirm={handleAdminOverride}
        confirmLabel={<Trans>Yes, Save Anyway</Trans>}
        variant="destructive"
        isLoading={isDataSubmitting}
      />
    </>
  );
}
